<template>
  <div class="container p-3">
    <Alert
      v-if="showAlert"
      :message="alertMessage"
      :alertType="alertType"
      :alertIcon="alertIcon"
      :closable="alertClosable"
      @on-close="dismissAlert"
    />
    <div v-if="loading" class="mt-3">
      <PageLoader
        :title="t('WAIT_A_MOMENT')"
        :subTitle="t('WAIT_VALIDATION_TEXT')"
      />
    </div>
    <div v-else>
      <CreditCheckContent
        v-if="showCreditCheckPermissionContent"
        :permissionContent="permissionContent"
      />

      <div v-if="useESignature" class="mt-3">
        <ESignAuth
          @switch-auth-method="handleSwitchAuthMethod"
          @change-signature="handleChangeSignature"
        />
      </div>

      <div class="row">
        <div class="col-12 text-center py-2">
          <ButtonContinue
            v-if="useESignature"
            class="btn-next"
            :isEnabled="isEnabledContinue || isCreditCheckPermitted"
            :label="t('CONTINUE')"
            @click="handleContinue"
          />
          <PreviousStepButton />
        </div>
      </div>
    </div>
  </div>
</template>
<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref, watchEffect } from "vue";
import { useRouter } from "vue-router";
import { useI18n } from "@/apps/merchant3/composables/useI18n";
import { useStore } from "vuex";
import Alert from "@/apps/merchant3/shared-components/common/Alert.vue";
import ESignAuth from "@/apps/merchant3/components/customer-registration-v2/credit-check-auth/ESignAuth.vue";

import { AxiosError, AxiosResponse } from "axios";
import { getCreditCheckPermission } from "@/apps/merchant3/services/creditCheckPermission.service";
import useUpdatePageState from "@/apps/merchant3/hooks/useUpdateCurrentPageState";
import useCartPersistState from "@/apps/merchant3/hooks/useCartPersistState";

import { openChatAfterSeconds } from "@/apps/merchant3/helpers/chatBotPopup";
import useIntervalCustomerState from "@/apps/merchant3/hooks/useIntervalCustomerState";

import { creditCheckSignature } from "@/apps/merchant3/services/creditCheckPermission.service";
import logEvent from "@/apps/merchant3/services/event-logs.service";
import EventLogs from "@/apps/merchant3/types/EventLogs";
import PreviousStepButton from "@/apps/merchant3/shared-components/ui/PreviousStepButton.vue";
import ButtonContinue from "@/apps/merchant3/shared-components/ui/ButtonContinue.vue";
import { getAPIErrorMessage } from "@/apps/merchant3/shared-helpers/APIErrorMessages";
import {
  AuthType,
  IAuthContinue,
} from "@/apps/merchant3/types/CreditCheckPermission";
import IErrorResponse from "@/apps/merchant3/types/IErrorResponse";
import { RouteName } from "@/apps/merchant3/router/RouteName";
import { MERCHANT_V3_API_URLS as M3_API } from "@/apps/merchant3/services/apiClient/apiUrl";
import { isRequestAlreadyDone } from "@/apps/merchant3/helpers/uniqueRequests.helper";
import { logEcommerceEvent } from "@/apps/merchant3/helpers/offers/ecommerce-event-log";
import PageLoader from "@/apps/merchant3/components/ui/loaders/PageLoader.vue";
import CreditCheckContent from "./CreditCheckContent.vue";
import useConfigurations from "@/apps/merchant3/hooks/useConfigurations";
import { useConfigurableNavigation } from "@/apps/merchant3/hooks/useConfigurableNavigation";
import { CartActiveFeatures } from "@/apps/merchant3/types/CartActiveFeatures";
import { getCountryCode } from "@/apps/merchant3/helpers/flowType";

const { t } = useI18n();
const { components, configResponse, globalComponents } = useConfigurations();
const { isConfigurableFlow, navigateToNextStep } = useConfigurableNavigation();

const alertType = ref("");
const alertMessage = ref("");
const alertIcon = ref("");
const showAlert = ref(false);
const alertClosable = ref(true);
const loading = ref(true);
const permissionContent = ref();
const isEnabledContinue = ref(false);
const eSignature = ref("");

const store = useStore();
const router = useRouter();

const signCreditCheckUrl = M3_API(store.state.carts.cartTag).SIGN_CREDIT_CHECK;

const cartTag = computed(() => store.state.carts.cartTag);
const locale = computed(() => configResponse.value?.defaultLanguage);
const useESignature = computed(() => components.value?.eSignature ?? false);

const showCreditCheckPermissionContent = computed(() => {
  // Always show credit check content if document is loaded
  // The document availability is controlled by the API, not configuration
  return !!permissionContent.value;
});
const showChatPopup = computed(() => {
  return globalComponents.value?.showChatPopup ?? false;
});
const isCreditCheckPermitted = computed(
  () => store.state.carts?.creditCheckPermittedTimestamp
);

const isKycReuseFlow = computed(() => {
  return store.getters["carts/hasActiveFeature"](
    CartActiveFeatures.SKIPPED_KYC
  );
});

const dismissAlert = () => (showAlert.value = false);

const getCheckCreditPermissionDocument = () => {
  if (!locale.value || !cartTag.value) {
    return false;
  }

  getCreditCheckPermission(cartTag.value, locale.value)
    .then((response: AxiosResponse) => {
      permissionContent.value = response.data?.document;
      logEvent(EventLogs.Events.CREDIT_CHECK_PERMISSION_DOC_LOADED);
      loading.value = false;
    })
    .catch(() => {
      let message = t("ERROR_SOMETHING_GOES_WRONG");
      handleSetAlert({
        message,
        messageType: "alert-danger",
        icon: "fa fa-exclamation-triangle",
      });
    });

  if (showChatPopup.value) {
    openChatAfterSeconds(300);
  }
  return true;
};
const handleSetAlert = ({
  message,
  messageType,
  icon,
}: {
  message: string;
  messageType: string;
  icon: string;
}) => {
  alertMessage.value = message;
  alertType.value = messageType;
  alertIcon.value = icon;
  showAlert.value = true;
};
const navigateToNextPage = () => {
  // For configurable flow, use backend-driven navigation
  if (isConfigurableFlow.value) {
    navigateToNextStep();
    return;
  }

  // For KYC Reuse flow, skip Contact Info and go to Offers
  if (isKycReuseFlow.value) {
    router.push({ name: RouteName.M3Offers, params: { countryCode: getCountryCode() } });
    return;
  }

  // Default: go to Contact Info
  router.push({ name: RouteName.M3CustomerContactInfoView, params: { countryCode: getCountryCode() } });
};

const handleSwitchAuthMethod = (authMethod: AuthType) => {
  if (authMethod === AuthType.SMS) {
    logEvent(EventLogs.Events.CREDIT_CHECK_PERMISSION_CLICKED_SMS_OPTION);
    navigateToNextPage();
  }
};
const handleChangeSignature = ({
  signature = "",
  enableContinue,
}: IAuthContinue) => {
  eSignature.value = signature;
  isEnabledContinue.value = signature !== "" && enableContinue;
};

const handleContinue = () => {
  logEvent(EventLogs.Events.CREDIT_PULL_CONTINUE_BTN_CLICKED);

  isEnabledContinue.value = false;

  if (
    !isCreditCheckPermitted.value &&
    !isRequestAlreadyDone(signCreditCheckUrl)
  ) {
    const form = new FormData();
    form.append("file", eSignature.value);
    logEvent(EventLogs.Events.CREDIT_CHECK_CALLED);

    creditCheckSignature(cartTag.value, form, locale.value)
      .then((response: AxiosResponse) => {
        store.dispatch("carts/setCart", response.data);
        logEvent(EventLogs.Events.CREDIT_CHECK_PERMITTED_BY_ESIGN);
        navigateToNextPage();
      })
      .catch((error: AxiosError<IErrorResponse>) => {
        const message = getAPIErrorMessage(error.response?.data, t);
        handleSetAlert({
          message,
          messageType: "alert-danger",
          icon: "fa fa-exclamation-triangle",
        });
      });
  } else {
    navigateToNextPage();
  }
};

onMounted(() => {
  useUpdatePageState();
  useCartPersistState(() => {
    logEvent(EventLogs.Events.CREDIT_PULL_SCREEN_LOADED);
    return true;
  });
  useIntervalCustomerState(1);
  logEcommerceEvent(EventLogs.Events.ECOMMERCE_BUREAU_CONSULT);
});

watchEffect(() => {
  if (locale.value && cartTag.value) {
    getCheckCreditPermissionDocument();
  }
});

onUnmounted(() => {
  logEvent(EventLogs.Events.CREDIT_PULL_SCREEN_LEFT);
});
</script>
<style lang="scss" scoped>
@import "@/assets/scss/_constants.scss";

.link {
  color: $pj-color-green;
  font-weight: 600;
  clear: both;
  text-decoration: none;
}
.link:hover {
  font-weight: 700;
}
</style>
